import { Link } from 'react-router-dom';
import React, { useState, useEffect, useContext } from 'react';
import logo from '../../assets/images/bluesky-vue-logo.png';
import classnames from 'classnames';
import LinearProgress from '@material-ui/core/LinearProgress';
import TextField from '@material-ui/core/TextField';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { verifyMfaCode, sendMfaCode, getMfaChallengeData } from '../../services';
import { AppContext } from '../../AppContext';
import { CircularProgress } from '@material-ui/core';

var { version: appVersion } = require('../../../package.json');

export const MfaVerification = props => {
  // State management
  const [code, setCode] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [mfaData, setMfaData] = useState(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const { state, dispatch } = useContext(AppContext);

  useEffect(() => {
   const fetchMfaData = async () => { 
    try {
      const tempToken = sessionStorage.getItem('mfaTempToken');
       if (!tempToken) throw new Error('Missing Token');

      const response = await getMfaChallengeData(tempToken);
      const emailChannel = response.availableChannels.find(c => c.type === "email");
      const smsChannel = response.availableChannels.filter(c => c.type === "sms");

      const mfaDataResponse = {
        maskedEmail: emailChannel?.maskedValue || "",
        maskedPhoneNumbers: smsChannel.map(c => c.maskedValue) || [] ,
        availableChannels: response.availableChannels,
      }

      setMfaData(mfaDataResponse);
      // Always show channel selection - user picks every time

    } catch (error) {
      props.history.push('/login');
    }
  }

  fetchMfaData();

  }, [props.history]);

  const handleSendCode = async () => {
    if (!selectedChannel) {
      setVerifyError('Please select a verification method');
      return;
    }

    setVerifyError('');
    setVerifyLoading(true);

    const tempToken = sessionStorage.getItem('mfaTempToken');

    try {
      const channel = mfaData.availableChannels.find(
      ch => ch.maskedValue === selectedChannel
    );
    
    if (!channel) {
      throw new Error('Invalid channel selection');
    }
    
      // For SMS, send only last 4 digits; for email, send the full masked value
      const identifier = channel.type === 'sms' 
        ? selectedChannel.replace(/\D/g, '').slice(-4) 
        : selectedChannel;
      
      await sendMfaCode(channel.type, identifier);
      setCodeSent(true);
      setVerifyLoading(false);
    } catch (error) {
      console.error(error);
      setVerifyLoading(false);
      setVerifyError(error.message || 'Failed to send verification code');
    }
  };

  const attemptVerification = async (evt, code) => {
    evt.preventDefault();
    setVerifyError('');
    setVerifyLoading(true);

    const tempToken = sessionStorage.getItem('mfaTempToken');
    
    if (!tempToken) {
      setVerifyError('Session expired. Please log in again.');
      setVerifyLoading(false);
      setTimeout(() => {
        props.history.push('/login');
      }, 2000);
      return;
     }

    try {
      const channel = mfaData.availableChannels.find(ch => ch.maskedValue === selectedChannel);
      if (!channel) {
        throw new Error('Invalid channel selection');
      }

      // For SMS, send only last 4 digits; for email, send the full masked value
      const identifier = channel.type === 'sms' 
        ? selectedChannel.replace(/\D/g, '').slice(-4) 
        : selectedChannel;

      const roles = await verifyMfaCode(identifier, code, channel.type, rememberDevice, 'browser');
      
      // Clear the temp data
      sessionStorage.removeItem('mfaTempToken');
      sessionStorage.removeItem('mfaData');

      dispatch({
        type: 'UPDATE_ROLES',
        roles,
      });

      setVerifyLoading(false);
      window.location = '/';
    } catch (error) {
      console.error(error);
      setVerifyLoading(false);
      setVerifyError(error.message || 'Invalid verification code. Please try again.');
    }
  };

  const handleCodeChange = evt => {
    const value = evt.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
    }
  };

  const handleBackToLogin = () => {
    sessionStorage.removeItem('mfaTempToken');
    sessionStorage.removeItem('mfaData');
    props.history.push('/login');
  };

  const handleChannelChange = evt => {
    console.log('value of event', evt.target.value)
    setSelectedChannel(evt.target.value);
    setCodeSent(false);
    setCode('');
    setVerifyError('');
  };

  const getChannelDisplayText = channel => {
    if (channel.type === 'email') {
      return `Email ${channel.maskedValue || 'your registered email'}`;
    }
    if (channel.type === 'sms') {
      const phoneNumbers = channel.maskedValue || [];
      if (phoneNumbers.length > 0) {
        return `Text Message ${channel.maskedValue}`;
      }
      return 'Text Message';
    }
    return channel.type;
  };

  const getSelectedChannelMasked = () => selectedChannel || '';

  if (!mfaData) {
    return (
      <div className={classnames('app', 'login-background', 'col-center-center')}>
        <div className={classnames('login-card')}>
          <div className={classnames('login-card-left', 'col-center-center')}>
            <img src={logo} alt={'BlueSky Vue Logo'} style={{ maxWidth: '100%', height: 'auto' }} />
          </div>
          <div className={classnames('login-card-right', 'col-center-center')}>
            <div
              style={{
                marginBottom: 10,
                fontWeight: 'bold',
                fontSize: 28,
                color: 'var(--jmi-blue)',
              }}
            >
              VERIFY IDENTITY
            </div>
            <div style={{ marginTop: 30 }}>
              <CircularProgress style={{ color: 'var(--jmi-blue)' }} size={60} />
            </div>
          </div>
        </div>
        <div style={{ marginTop: 25, color: '#aaa', fontSize: 14 }}>Version {appVersion}</div>
      </div>
    );
  }

  const hasAvailableChannels = mfaData.availableChannels && mfaData.availableChannels.length > 0;

  return (
    <div className={classnames('app', 'login-background', 'col-center-center')}>
      <div className={classnames('login-card')}>
        <div className={classnames('login-card-left', 'col-center-center')}>
          <img src={logo} alt={'BlueSky Vue Logo'} style={{ maxWidth: '100%', height: 'auto' }} />
        </div>
        <div className={classnames('login-card-right', 'col-center-center')}>
          <div
            style={{
              marginBottom: 10,
              fontWeight: 'bold',
              fontSize: 28,
              color: 'var(--jmi-blue)',
            }}
          >
            VERIFY IDENTITY
          </div>
          
 

          {!hasAvailableChannels ? (
            <div
              style={{
                marginBottom: 20,
                fontSize: 14,
                color: '#d32f2f',
                textAlign: 'center',
                width: '90%',
                padding: '15px',
                background: '#ffebee',
                borderRadius: '4px',
              }}
            >
              No verification methods are available for your account. Please contact your administrator.
            </div>
          ) : !codeSent ? (
            // Channel Selection View
            <div style={{ width: '90%' }}>
              <div
                style={{
                  marginBottom: 15,
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                Choose how you want to receive your verification code:
              </div>

              <RadioGroup
                value={selectedChannel}
                onChange={handleChannelChange}
                style={{ marginBottom: 20 }}
              >
                {mfaData.availableChannels.map((channel, index) => (
                  <FormControlLabel
                    key={index}
                    value={channel.maskedValue}
                    control={<Radio style={{ color: 'var(--jmi-blue)' }} />}
                    label={getChannelDisplayText(channel)}
                    style={{
                      marginBottom: 10,
                      padding: '10px',
                      border: selectedChannel === channel.maskedValue ? '2px solid var(--jmi-blue)' : '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: selectedChannel === channel.maskedValue ? '#f0f7ff' : 'transparent',
                    }}
                  />
                ))}
              </RadioGroup>

              <div style={{ marginTop: 20 }} className={classnames('row-between-center', 'full-width')}>
                <button
                  type="button"
                  className="login-submit-button"
                  style={{ background: '#ccc', color: '#333' }}
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </button>
                <button
                  type="button"
                  className="login-submit-button"
                  onClick={handleSendCode}
                  disabled={!selectedChannel || verifyLoading}
                >
                  Send Code
                </button>
              </div>
            </div>
          ) : (
            // Code Verification View
            <div style={{ width: '90%' }}>
              <div
                style={{
                  marginBottom: 20,
                  fontSize: 14,
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                Enter the 6-digit verification code sent to {getSelectedChannelMasked()}
              </div>

              <form id="mfa-form" onSubmit={evt => attemptVerification(evt, code)}>
                <div className="col-center-center">
                  <TextField
                    value={code}
                    margin="dense"
                    variant="outlined"
                    placeholder="000000"
                    inputProps={{
                      'aria-label': 'verification code',
                      maxLength: 6,
                      style: {
                        fontSize: 24,
                        letterSpacing: '0.5em',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      },
                    }}
                    style={{ width: '100%' }}
                    onChange={handleCodeChange}
                    autoFocus
                  />
                </div>

                <div style={{ marginTop: 15, display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        name="rememberDevice"
                        color="primary"
                      />
                    }
                    label="Remember this device"
                    style={{ fontSize: '13px' }}
                  />
                </div>

                <div style={{ marginTop: 20 }} className={classnames('row-between-center', 'full-width')}>
                  <button
                    type="button"
                    className="login-submit-button"
                    style={{ background: '#ccc', color: '#333', fontSize: '13px', padding: '8px 12px' }}
                    onClick={() => {
                      setCodeSent(false);
                      setCode('');
                      setVerifyError('');
                    }}
                  >
                    Change Method
                  </button>
                  <button
                    type="submit"
                    className="login-submit-button"
                    disabled={code.length !== 6 || verifyLoading}
                  >
                    Verify
                  </button>
                </div>

                <div style={{ marginTop: 15, textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={verifyLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--jmi-blue)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    Resend Code
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="login-card-submit-details">
            {verifyError && <div className="login-card-error">{verifyError}</div>}
            {verifyLoading && <LinearProgress className="login-card-loading" />}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 25, color: '#aaa', fontSize: 14 }}>Version {appVersion}</div>
    </div>
  );
};

MfaVerification.propTypes = {};
